import DatasetTable from "@/components/DatasetTable/DatasetTable";
import { EcoInventSuggestions } from "@/components/ImpactReferenceSelector/EcoInventSuggestions";
import ImpactReferenceModal from "@/components/ImpactReferenceSelector/ImpactReferenceModal";
import { tabItems } from "@/components/ImpactReferenceSelector/ImpactReferencePageTabs";
import Skeleton from "@/components/Skeleton/Skeleton";
import useAnalytics from "@/hooks/useAnalytics";
import { ImpactDataset } from "@/hooks/useEcoInventDatasets";
import useTranslation from "@/hooks/useTranslation";
import styles from "@/pages/materials/MaterialImpactModal/MaterialImpactReferenceModal.module.scss";
import { DeleteOutlined } from "@ant-design/icons";
import { Button, message } from "antd";
import { useEffect, useState } from "react";
import { useBulkPatchMaterial, useGetMaterial } from "../useMaterials";
import DeleteMaterialImpactReferenceAction from "./DeleteMaterialImpactReference";

export interface MaterialImpactReferenceModalProps {
  state: "add" | "edit";
  disabled?: boolean;
  materialIds: number[];
  onClose: () => void;
}

export default function MaterialImpactModal({
  state,
  materialIds,
  onClose,
}: MaterialImpactReferenceModalProps) {
  const { data: material, isFetching: materialIsFetching } = useGetMaterial({
    materialId: materialIds[0],
    enabled: materialIds.length === 1,
  });
  const analytics = useAnalytics();
  const translation = useTranslation();
  const bulkPatch = useBulkPatchMaterial();
  const [selectedDataset, setSelectedDataset] = useState<ImpactDataset>();
  const [activeTab, setActiveTab] = useState<string>(tabItems.suggestions.key);

  const onDatasetChange = (dataset?: ImpactDataset) => {
    setSelectedDataset(dataset);
  };

  const onSubmit = () => {
    if (selectedDataset) {
      bulkPatch
        .mutateAsync({
          materialIds,
          patch: {
            impact_dataset_id: selectedDataset.id,
          },
        })
        .then(() => {
          analytics.track(`Impact Reference ${state === "edit" ? "Updated" : "Created"}`);
          message.success(
            `Impact reference${materialIds.length > 1 ? "s" : ""} ${
              state === "edit" ? "updated" : "created"
            }.`
          );
          onClose?.();
        })
        .catch(() => {
          message.error(translation.errors.general);
        });
    }
  };

  const onTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const CurrentSelectionContent = (
    <div className={styles["current-selection"]}>
      <span className={styles.title}>For material{materialIds.length === 1 ? "" : "s"}</span>
      <span className={styles.material}>
        {materialIds.length > 1 ? (
          `${materialIds.length} selected`
        ) : !material || materialIsFetching ? (
          <Skeleton style={{ width: 150, height: 24 }} />
        ) : (
          [material?.name, material?.supplier_name, material?.supplier_address]
            .filter((x) => x)
            .join(" • ")
        )}
      </span>
      <span
        className={styles.title}
        style={{ marginTop: 4 }}
      >
        Currently saved impact reference
      </span>
      {materialIds.length > 1 ? (
        <span className={styles.material}>
          Selecting and saving a reference will overwrite any saved references for the selected
          materials.
        </span>
      ) : !material || materialIsFetching ? (
        <Skeleton style={{ width: 300, height: 40 }} />
      ) : material?.match ? (
        <DatasetTable
          type="grey"
          data={material?.match && [material.match]}
          emptyState={{ noResultsMessage: <span>No reference selected yet.</span> }}
          hideHeader={true}
          hideRaster={true}
          activeRowKey={selectedDataset?.id}
          onRowClick={(record) => setSelectedDataset(record)}
        />
      ) : (
        <span className={styles.material}>No reference saved yet.</span>
      )}
    </div>
  );

  const DeleteAction = (
    <DeleteMaterialImpactReferenceAction
      materialIds={materialIds}
      onSuccess={() => onClose()}
    >
      <Button
        disabled={bulkPatch.isLoading}
        danger
      >
        <DeleteOutlined />
      </Button>
    </DeleteMaterialImpactReferenceAction>
  );

  useEffect(() => {
    if (material?.match && materialIds?.length === 1) {
      setSelectedDataset(material.match);
    }
    if (materialIds && materialIds.length > 1) {
      setActiveTab(tabItems.ecoinvent.key);
    }
  }, [material, materialIds]);

  return (
    <ImpactReferenceModal
      title={state === "add" ? "Assign impact reference" : "Edit impact reference"}
      onClose={onClose}
      onSubmit={onSubmit}
      currentSelectionContent={CurrentSelectionContent}
      bottomLeft={DeleteAction}
      isLoading={bulkPatch.isLoading}
      selectedDataset={selectedDataset}
      onDatasetChange={onDatasetChange}
      activeTab={activeTab}
      onTabChange={onTabChange}
      suggestionTabContent={
        <EcoInventSuggestions
          materialIds={materialIds}
          selectedDataset={selectedDataset}
          onDatasetChange={onDatasetChange}
          setActiveTab={setActiveTab}
        />
      }
      defaultDatabaseFilter={{ special_activity_type: ["market activity"] }}
    />
  );
}
